import { createContext, useContext } from "react";
import { useSession } from "session";

/**
 * Whether the order on screen was placed from the forex desk. Core keeps line,
 * charge, note, delivery and payment-link changes to those orders; one from the
 * website, a partner or the old system has no forex details for them to work on.
 * Outside an order's page there is no order to ask about, so it reads true.
 */
export const DeskPlacedOrderContext = createContext(true);

/**
 * What the signed-in person may do on a forex order.
 *
 * Core refuses a forex write unless the caller holds `forex.desk` (admin,
 * ops_manager and ops) as well as the permission the action itself needs. A
 * control a person cannot use is not shown, rather than shown greyed out, so a
 * finance user sees the payment controls and nothing else. Core still decides:
 * this only stops the screen offering what would be refused.
 */
export function useForexAccess() {
  const { can } = useSession();
  const deskPlaced = useContext(DeskPlacedOrderContext);
  const desk = can("forex.desk");
  const canWrite = desk && can("order.write");
  const canStatus = desk && can("order.status");
  // What only works on an order placed from the desk.
  const canWriteDesk = canWrite && deskPlaced;
  const canStatusDesk = canStatus && deskPlaced;
  return {
    /** Notes, coupon, payment link, deliveries, cards, rate and quantity edits. */
    canEditOrder: canWriteDesk,
    /** Upload, request and review the order's documents: any order. */
    canEditDocuments: canWrite,
    /** Cancel the order, or move its status on: any order. */
    canDecide: canStatus,
    canEditTcs: canStatusDesk,
    canEditServiceCharge: desk && can("order.discount") && deskPlaced,
    canChangeDelivery: canWriteDesk,
    canChangeDeliveryStatus: canWriteDesk && can("order.status"),
    /** Finance and the desk may record and decide payments; nobody else may. */
    canHandlePayments: can("payment.record") || desk,
    canRefund: can("payment.record"),
  };
}
